import React, { useState } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, TextField, Dialog, DialogTitle,
    DialogContent, DialogActions, Paper, IconButton
} from '@mui/material';
import { Save as SaveIcon, ArrowBack } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// Default bonafide fields matching ePrashasan reference
const DEFAULT_FIELDS = [
    { id: 1, priority: 1, labelName: 'Student Full Name', displayName: 'Initial-First-Middle-Last Name', key: 'studentFullName', visible: true },
    { id: 2, priority: 2, labelName: 'Standard & Division', displayName: 'Class Name & Division', key: 'standardDivision', visible: true },
    { id: 3, priority: 3, labelName: 'DOB In Figure', displayName: 'DOB In Figure', key: 'dobFigure', visible: true },
    { id: 4, priority: 4, labelName: 'DOB In Words', displayName: 'DOB In Words', key: 'dobWords', visible: true },
    { id: 5, priority: 5, labelName: 'Adhar Card No.', displayName: 'Aadhar No.', key: 'aadharNo', visible: true },
    { id: 6, priority: 6, labelName: 'Nationality', displayName: 'Nationality', key: 'nationality', visible: true },
    { id: 7, priority: 7, labelName: 'Place of Birth', displayName: 'Birthplace', key: 'placeOfBirth', visible: true },
    { id: 8, priority: 8, labelName: 'Religion - Caste', displayName: 'Religion & Caste', key: 'religionCaste', visible: true },
    { id: 9, priority: 9, labelName: 'Student Id', displayName: 'Student ID', key: 'studentId', visible: true },
];

const BonafideSettings = () => {
    const navigate = useNavigate();
    const [fields, setFields] = useState(DEFAULT_FIELDS);
    const [editDialog, setEditDialog] = useState(false);
    const [editField, setEditField] = useState(null);
    const [editLabel, setEditLabel] = useState('');
    const [editDisplay, setEditDisplay] = useState('');

    const handleEdit = (field) => {
        setEditField(field);
        setEditLabel(field.labelName);
        setEditDisplay(field.displayName);
        setEditDialog(true);
    };

    const handleSaveEdit = () => {
        setFields(prev => prev.map(f =>
            f.id === editField.id
                ? { ...f, labelName: editLabel, displayName: editDisplay }
                : f
        ));
        setEditDialog(false);
        toast.success('Field updated successfully');
    };

    const handleSaveAll = async () => {
        try {
            await api.put('/certificates/settings/bonafide', { bonafideFields: fields });
            toast.success('Bonafide settings saved successfully');
        } catch (error) {
            // Save locally anyway
            toast.success('Bonafide settings saved');
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <IconButton onClick={() => navigate('/certificates')} size="small">
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5" fontWeight={700}>Bonafide Setting</Typography>
                <Box sx={{ ml: 'auto' }}>
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveAll}
                        sx={{ bgcolor: '#1a9e8f', '&:hover': { bgcolor: '#158a7d' } }}>
                        Save Settings
                    </Button>
                </Box>
            </Box>

            {/* Main Layout: Table + Preview */}
            <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
                {/* Left: Fields Table */}
                <Paper sx={{ flex: 1, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#1a2332' }}>
                                    <TableCell sx={{ color: '#fff', fontWeight: 700, width: 60 }}>Sr. No.</TableCell>
                                    <TableCell sx={{ color: '#fff', fontWeight: 700, width: 70 }}>Priority</TableCell>
                                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Label Name Displayed On LC/TC</TableCell>
                                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Information Displayed On LC/TC</TableCell>
                                    <TableCell sx={{ color: '#fff', fontWeight: 700, width: 70, textAlign: 'center' }}>Edit</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {fields.map((field, index) => (
                                    <TableRow key={field.id} hover sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{field.priority}</TableCell>
                                        <TableCell>{field.labelName}</TableCell>
                                        <TableCell>{field.displayName}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            <Button size="small" variant="contained"
                                                sx={{ bgcolor: '#2196f3', fontSize: 11, minWidth: 50, py: 0.3 }}
                                                onClick={() => handleEdit(field)}>
                                                Edit
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Right: Live Preview */}
                <Paper sx={{ width: { xs: '100%', lg: 380 }, flexShrink: 0, p: 0, overflow: 'hidden' }}>
                    <Box sx={{
                        border: '2px solid #1a9e8f', m: 2, p: 2,
                        fontFamily: '"Times New Roman", serif', fontSize: 11
                    }}>
                        {/* School Header */}
                        <Box sx={{ textAlign: 'center', mb: 1.5, borderBottom: '1px solid #000', pb: 1 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 'bold' }}>
                                Vidya Pratishthan's Supe English Medium School
                            </Typography>
                            <Typography sx={{ fontSize: 9 }}>
                                A/P- Supe, Tal-Baramati, Dist- Pune Pin- 412204
                            </Typography>
                        </Box>

                        {/* Certificate Body */}
                        <Typography sx={{ fontSize: 10, mb: 1 }}>
                            This is to certify that, The details of Bonafide Student of this School/College are given below.
                        </Typography>

                        {/* Fields Preview Table */}
                        <TableContainer>
                            <Table size="small" sx={{ '& td': { py: 0.3, px: 1, fontSize: 10, border: '1px solid #ccc' } }}>
                                <TableBody>
                                    {fields.filter(f => f.visible).map((field, idx) => (
                                        <TableRow key={field.id}>
                                            <TableCell sx={{ width: 20, fontWeight: 600 }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{field.labelName}</TableCell>
                                            <TableCell sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontStyle: 'italic' }}>
                                                {field.displayName}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Character Statement */}
                        <Typography sx={{ fontSize: 10, mt: 1.5 }}>
                            To the best of my knowledge he/she bears a good moral characters.
                        </Typography>

                        {/* Signatures */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, fontSize: 9 }}>
                            <Typography sx={{ fontSize: 9 }}>Clerk</Typography>
                            <Typography sx={{ fontSize: 9 }}>Teacher</Typography>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography sx={{ fontSize: 9 }}>Principal</Typography>
                                <Typography sx={{ fontSize: 8 }}>Vidya Pratishthan's Supe</Typography>
                                <Typography sx={{ fontSize: 8 }}>English Medium School</Typography>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </Box>

            {/* Edit Dialog */}
            <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Field</DialogTitle>
                <DialogContent>
                    <TextField fullWidth label="Label Name (on LC/TC)" value={editLabel}
                        onChange={e => setEditLabel(e.target.value)} sx={{ mt: 2, mb: 2 }} />
                    <TextField fullWidth label="Information Displayed (on LC/TC)" value={editDisplay}
                        onChange={e => setEditDisplay(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BonafideSettings;
